/**
 * segmentFillAnimation.js — render-side animated-fill PLAYBACK POLICY.
 *
 * WL-058 (Fable Hail Mary, cool-pass restyle sweep). Implements the "wedge
 * animation / GIF options as designed" render-side behaviour that the editor
 * layer (`segmentFill.js`, WL-012) intentionally left out. `segmentFill.js`
 * answers "what STATIC background does a fill produce, and should a GIF show
 * its poster or its animated source?" (`resolveGifPlayback`). This module adds
 * the two things a *renderer* additionally needs and the deck specifies:
 *
 *   1. The GPU-only PAN itself (deck `concepts/segment-fills` round 4: while a
 *      Segment is open, a wedge-clipped copy of its GIF "pans (CSS translate,
 *      9s alternate) over the open arc"). We express the pan as a Web-Animations
 *      keyframe array + timing so the consumer drives it with `element.animate`
 *      — `transform` only, so it composites on the GPU (never `background-position`,
 *      which forces main-thread repaint).
 *
 *   2. The PERFORMANCE GUARDRAILS the design direction makes non-negotiable
 *      (DESIGN-DECISIONS-LOG row 7 / COOL-FACTOR-DESIGN-DIRECTION §guardrails):
 *      `prefers-reduced-motion` kills the pan (poster frame), animations PAUSE
 *      while their Segment is offscreen, and a CONCURRENT CAP limits how many
 *      fills animate at once (a Gallery of Wheels must never run dozens of pans).
 *
 * PURE + DETERMINISTIC: no React, no DOM, no I/O, no timers. Everything here is
 * a testable function of its inputs. The thin DOM/React glue (IntersectionObserver
 * offscreen detection, `matchMedia` reduced-motion, and `element.animate`) lives
 * in the `useSegmentFillAnimation` hook, which delegates every DECISION here.
 *
 * REUSE, NOT DUPLICATION: the GIF poster/animate/chip resolution is delegated to
 * `resolveGifPlayback` in `segmentFill.js` — this module never re-implements it,
 * it only layers the pan + guardrail policy on top.
 *
 * G-19 NOTE: draws NO Wheel and emits no `<path>`/SVG. It returns transform
 * keyframes + timing that the caller applies to ONE Segment's own fill element
 * inside the canonical renderer.
 */

import { resolveGifPlayback } from "./segmentFill";

/**
 * Fill types that can ANIMATE on the arc. Per the deck only GIF pans; Image is a
 * static photo scene, Color/Gradient/Pattern are static paint. Kept as a list so
 * a future animatable type (e.g. a looping video fill) is a one-line addition.
 */
export const ANIMATABLE_FILL_TYPES = ["gif"];

/**
 * How many fills may animate simultaneously (row-7 "concurrent cap"). The open
 * Segment always wins its slot; on a normal View-Wheel at most one Segment is
 * open so the effective count is ≤1, but a Gallery/showcase surface can request
 * a higher cap deliberately. `1` is the safe default.
 */
export const DEFAULT_CONCURRENT_CAP = 1;

/** Pan timing (deck round 4: "9s alternate"). */
export const PAN_DURATION_MS = 9000;
export const PAN_DIRECTION = "alternate";
export const PAN_EASING = "ease-in-out";

/**
 * Base up-scale applied before the pan so the arc is fully covered at both
 * travel extremes (no bare edge peeks in as the fill slides). 1.16 gives ±6%
 * travel head-room on each axis with margin to spare.
 */
export const PAN_SCALE = 1.16;

/** Default horizontal travel as a fraction of the fill element's own width. */
export const DEFAULT_PAN_FRACTION = 0.06;

const clamp = (n, lo, hi, fallback) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.min(hi, Math.max(lo, x));
};

/** Is this fill an animatable type at all (regardless of open/motion state)? */
export const isAnimatableFill = (fill) =>
  ANIMATABLE_FILL_TYPES.includes((fill || {}).type);

/**
 * Build the GPU-only pan keyframes for `element.animate()`.
 *
 * Two-keyframe transform-only pan: scale up, then translate from -f% to +f% of
 * the element's own width. Because `translateX(%)` is resolved against the
 * element's box, this needs no pixel measurement and is resolution-independent.
 * Using `transform` (not `background-position`) keeps the whole animation on the
 * compositor thread.
 *
 * @param {{ panFraction?:number, scale?:number, axis?:"x"|"y" }} [opts]
 * @returns {Array<{transform:string}>} 2 keyframes (start ↔ end); `direction:
 *          alternate` in the timing makes it travel back and forth seamlessly.
 */
export const buildPanKeyframes = (opts = {}) => {
  const scale = clamp(opts.scale, 1, 2, PAN_SCALE);
  const f = clamp(opts.panFraction, 0, 0.25, DEFAULT_PAN_FRACTION);
  const pct = Math.round(f * 100 * 100) / 100; // 2dp, e.g. 6
  const axis = opts.axis === "y" ? "y" : "x";
  const t = (sign) =>
    axis === "y"
      ? `scale(${scale}) translateY(${sign}${pct}%)`
      : `scale(${scale}) translateX(${sign}${pct}%)`;
  return [{ transform: t("-") }, { transform: t("") }];
};

/**
 * Build the Web-Animations timing options for the pan.
 *
 * @param {{ durationMs?:number }} [opts]
 * @returns {{duration:number, direction:string, easing:string, iterations:number}}
 */
export const buildPanTiming = (opts = {}) => ({
  duration: clamp(opts.durationMs, 500, 60000, PAN_DURATION_MS),
  direction: PAN_DIRECTION,
  easing: PAN_EASING,
  iterations: Infinity,
});

/**
 * Resolve the full playback decision for ONE fill in a render context — the
 * single entry point a renderer calls per Segment.
 *
 * Layers the pan policy on top of `resolveGifPlayback`. `running` is the key
 * new signal: TRUE only when the fill is animatable, its Segment is open, the
 * viewer allows motion, AND the Segment is on screen (offscreen ⇒ paused, not
 * stopped — the animation object is kept, just not running, so state survives a
 * scroll back into view).
 *
 * @param {object} fill  a Segment fill ({ type, gif?, ... })
 * @param {{open?:boolean, reducedMotion?:boolean, onScreen?:boolean,
 *          panFraction?:number, durationMs?:number}} [ctx]
 * @returns {{
 *   animate:boolean,      // GIF would animate if unpaused (open + motion-ok)
 *   running:boolean,      // should be actively panning RIGHT NOW (adds onScreen)
 *   paused:boolean,       // animatable + open but held (offscreen)
 *   src:string,           // poster vs animated source (from resolveGifPlayback)
 *   showChip:boolean,     // corner "GIF" chip (closed only)
 *   keyframes:(Array|null),
 *   timing:(object|null)
 * }}
 */
export const resolveFillPlayback = (fill, ctx = {}) => {
  const onScreen = ctx.onScreen !== false; // default true (assume visible)
  const gifState = { open: ctx.open, reducedMotion: ctx.reducedMotion };

  if (!isAnimatableFill(fill)) {
    // Non-animatable fills never pan; still surface a stable, inert shape.
    return {
      animate: false,
      running: false,
      paused: false,
      src: "",
      showChip: false,
      keyframes: null,
      timing: null,
    };
  }

  const play = resolveGifPlayback((fill || {}).gif, gifState);
  const running = play.animate && onScreen;
  const paused = play.animate && !onScreen;
  return {
    animate: play.animate,
    running,
    paused,
    src: play.src,
    showChip: play.showChip,
    keyframes: play.animate
      ? buildPanKeyframes({ panFraction: ctx.panFraction })
      : null,
    timing: play.animate ? buildPanTiming({ durationMs: ctx.durationMs }) : null,
  };
};

/**
 * Resolve the ACTIVE animation set across MANY fills, honouring the concurrent
 * cap. For multi-Wheel surfaces (Gallery, showcase) that could otherwise start
 * an unbounded number of pans at once.
 *
 * Eligibility per entry = animatable ∧ open ∧ onScreen ∧ !reducedMotion. Among
 * the eligible, the first `cap` (stable input order — callers order by
 * importance, e.g. the focused Wheel first) become `active:true`; the rest are
 * `capped:true` (eligible but over budget → held as poster until a slot frees).
 *
 * @param {Array<{id:*, fill:object, open?:boolean, onScreen?:boolean}>} entries
 * @param {{reducedMotion?:boolean, concurrentCap?:number}} [ctx]
 * @returns {{
 *   activeIds:Set,
 *   entries:Array<{id:*, active:boolean, eligible:boolean, capped:boolean}>
 * }}
 */
export const resolveAnimationSet = (entries, ctx = {}) => {
  const list = Array.isArray(entries) ? entries : [];
  const reducedMotion = !!ctx.reducedMotion;
  const cap = Number.isFinite(ctx.concurrentCap)
    ? Math.max(0, Math.floor(ctx.concurrentCap))
    : DEFAULT_CONCURRENT_CAP;

  const activeIds = new Set();
  let used = 0;
  const out = list.map((e) => {
    const entry = e || {};
    const eligible =
      !reducedMotion &&
      isAnimatableFill(entry.fill) &&
      !!entry.open &&
      entry.onScreen !== false;
    let active = false;
    if (eligible && used < cap) {
      active = true;
      used += 1;
      activeIds.add(entry.id);
    }
    return { id: entry.id, active, eligible, capped: eligible && !active };
  });

  return { activeIds, entries: out };
};
